import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Heading } from "../components/heading";
import { Text } from "../components/text";
import { MonthlyProduction } from "./reportes/MonthlyProduction";
import { useTarifas, useContracts, useAsociados } from "../contexts/DataContext";
import { ComisionPorAfianzadora } from "./reportes/ComisionPorAfianzadora";
import { ComparativoAnual } from "./reportes/ComparativoAnual";
import { ReportePendientes } from "./reportes/ReportePendientes";
import { ProduccionPorResponsable } from "./reportes/ProduccionPorResponsable";
import { ProduccionPorCliente } from "./reportes/ProduccionPorCliente";
import { ProduccionPorAfianzadora } from "./reportes/ProduccionPorAfianzadora";
import { TarifasPorCliente } from "./reportes/TarifasPorCliente";
import { LiquidacionComisiones } from "./reportes/LiquidacionComisiones";
import { CobranzaPrimas } from "./reportes/CobranzaPrimas";
import { SaldosCreditosClientes } from "./reportes/SaldosCreditosClientes";
import { MetasPorAfianzadora } from "./reportes/MetasPorAfianzadora";
import { CicloVidaFianzas } from "./reportes/CicloVidaFianzas";
import { Beneficiarios } from "./reportes/Beneficiarios";
import { CumplimientoConvenios } from "./reportes/CumplimientoConvenios";
import { ProveedoresRiesgo } from "./reportes/ProveedoresRiesgo";
import { ComisionPorConvenio } from "./reportes/ComisionPorConvenio";
import { ComisionPorAsociado } from "./reportes/ComisionPorAsociado";
import { CoberturaPorProyecto } from "./reportes/CoberturaPorProyecto";
import { EstadisticasCliente } from "./reportes/EstadisticasCliente";
import { useUser } from "../components/UserContext";
import { ShieldExclamationIcon, DocumentArrowDownIcon, ChevronDownIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { getFechaEmision } from "../utils/calculations";
import { ExportPDFDialog } from "../components/ExportPDFDialog";
import { createPDFDocument, formatPeriod } from "../utils/pdfExport";
import "../styles/pdf-export.css";

// `operator: true` marks the reportes an operator may open: the ones with no
// comisión, no tarifa and no teammate comparison — cobranza, saldos y cartera,
// which is the work an operator actually does. Everything else stays with the
// owner. Note this is a relevance gate, not a security boundary: DataContext
// already loads every org client, contract and comisión movement for operators
// (the Firestore rules allow it), so hiding a report hides the presentation,
// not the data.
// Two tabs: the cartera reportes (clientes, afianzadoras, equipo) and the
// convenios de proveedores, which answer a different question with a different
// entity. Inside each tab the reportes are grouped by theme and live in a rail
// on the left: a grid of 17 cards above the report was a wall you scrolled past
// every time, and it pushed the report itself below the fold.
const TABS = [
  { id: "general", label: "General" },
  { id: "convenios", label: "Convenios" },
];

// Render order of the sections inside each tab.
const GROUPS = [
  { id: "produccion", tab: "general", title: "Producción" },
  { id: "comisiones", tab: "general", title: "Comisiones" },
  { id: "cobranza", tab: "general", title: "Cobranza y saldos" },
  { id: "cartera", tab: "general", title: "Cartera" },
  { id: "cumplimiento", tab: "convenios", title: "Cumplimiento" },
  { id: "dinero-convenios", tab: "convenios", title: "Comisión" },
];

// `usesYear: false` marks the reportes that are a snapshot of TODAY, not of an
// ejercicio. The year selector stays mounted for them (hiding it made the rail
// jump between reportes and read as a bug) but is disabled, so the control
// keeps its place and says why it does nothing.
const REPORTS = [
  // ── Producción ──────────────────────────────────────────────────────────
  {
    id: "monthly-production",
    tab: "general",
    group: "produccion",
    title: "Producción mensual",
    description: "Fianzas por mes con comisiones estimadas y resumen anual.",
  },
  {
    id: "produccion-responsable",
    tab: "general",
    group: "produccion",
    title: "Producción por responsable",
    description: "Producción y comisiones por miembro del equipo. Incluye clientes sin asignar.",
  },
  {
    id: "produccion-cliente",
    tab: "general",
    group: "produccion",
    title: "Producción por cliente",
    description: "Producción y comisiones por cliente. Visualiza qué clientes generan más.",
  },
  {
    id: "produccion-afianzadora",
    tab: "general",
    group: "produccion",
    title: "Producción por afianzadora",
    description: "Producción y comisiones por afianzadora. Visualiza qué emisores generan más.",
  },
  // ── Comisiones ──────────────────────────────────────────────────────────
  {
    id: "comparativo-anual",
    tab: "general",
    group: "comisiones",
    title: "Comparativo anual",
    description: "El año contra el anterior al mismo corte: comisión, prima y producción, quién explica la variación, mix y concentración.",
  },
  {
    id: "comision-afianzadora",
    tab: "general",
    group: "comisiones",
    title: "Comisión por afianzadora",
    description: "Resumen de comisiones estimadas por afianzadora en un mes o año.",
  },
  {
    id: "liquidacion-comisiones",
    tab: "general",
    group: "comisiones",
    title: "Liquidación de comisiones",
    description: "Comisión cobrada vs pendiente y cargos (ledger), netos por afianzadora o cliente.",
  },
  {
    id: "comision-asociado",
    tab: "general",
    group: "comisiones",
    // Only with orgs.asociadosEnabled (Surelia-set); owner-only like the rest.
    asociados: true,
    title: "Comisión por asociado",
    description: "Lo que le toca a cada asociado y socio en el mes, con el pago a asociados.",
  },
  {
    id: "metas-afianzadora",
    tab: "general",
    group: "comisiones",
    title: "Metas por afianzadora",
    description: "Avance mensual de prima contra la meta anual de cada afianzadora.",
  },
  {
    id: "tarifas-cliente",
    tab: "general",
    group: "comisiones",
    title: "Tarifas por cliente",
    description: "Análisis de tarifas promedio por cliente y sus fianzas.",
  },
  // ── Cobranza y saldos ───────────────────────────────────────────────────
  {
    id: "cobranza-primas",
    tab: "general",
    group: "cobranza",
    operator: true,
    usesYear: false,
    title: "Cobranza de primas",
    description: "Facturas sin pagar por antigüedad, con riesgo de buró, por cliente o afianzadora.",
  },
  {
    id: "pendientes",
    tab: "general",
    group: "cartera",
    operator: true,
    usesYear: false,
    title: "Pendientes",
    description: "Lo que sigue abierto hoy: por fiado, asignado, tipo o afianzadora, con vencidos y antigüedad.",
  },
  {
    id: "saldos-clientes",
    tab: "general",
    group: "cobranza",
    operator: true,
    usesYear: false,
    title: "Saldos de clientes",
    description: "Saldo por pagar y a favor por cliente y afianzadora, con créditos vivos.",
  },
  // ── Cartera ─────────────────────────────────────────────────────────────
  {
    id: "estadisticas-cliente",
    tab: "general",
    group: "cartera",
    operator: true,
    title: "Estadísticas de la cartera",
    description: "Distribución, historial por año y vencimientos de toda la cartera, o de un cliente.",
  },
  {
    id: "ciclo-vida",
    tab: "general",
    group: "cartera",
    operator: true,
    title: "Ciclo de vida de fianzas",
    description: "Emisiones, renovaciones, sustituciones, cancelaciones y anulaciones por mes.",
  },
  {
    id: "beneficiarios",
    tab: "general",
    group: "cartera",
    operator: true,
    title: "Beneficiarios",
    description: "A quién garantizan las fianzas del año: montos, clientes y afianzadoras por beneficiario.",
  },
  // ── Convenios ───────────────────────────────────────────────────────────
  {
    id: "cumplimiento-convenios",
    tab: "convenios",
    group: "cumplimiento",
    operator: true,
    usesYear: false,
    title: "Cumplimiento por convenio",
    description: "Qué exige cada convenio de proveedores y qué está emitido: atrasadas, por emitir y cobertura.",
  },
  {
    id: "proveedores-riesgo",
    tab: "convenios",
    group: "cumplimiento",
    operator: true,
    usesYear: false,
    title: "Proveedores en riesgo",
    description: "Proveedores con fianzas atrasadas, por emitir o vencidas, consolidados entre convenios.",
  },
  {
    id: "cobertura-proyecto",
    tab: "convenios",
    group: "cumplimiento",
    operator: true,
    usesYear: false,
    title: "Cobertura por proyecto",
    description: "Monto contratado contra garantizado por proyecto del convenio, con vencimientos a 90 días.",
  },
  {
    id: "comision-convenio",
    tab: "convenios",
    group: "dinero-convenios",
    title: "Comisión por convenio",
    description: "Comisión bruta, participación cedida y neta de las fianzas de convenios, del ledger.",
  },
];

// Un renglón del rail. La descripción no se pinta aquí — vive en el encabezado
// del reporte seleccionado — pero sí va en el title, para el hover.
const ReportRow = ({ report, selected, onSelect }) => {
  return (
    <button
      onClick={() => onSelect(report.id)}
      title={report.description}
      aria-current={selected ? "page" : undefined}
      className={`w-full text-left rounded-lg border-l-2 px-2.5 py-1.5 text-sm transition-colors ${
        selected
          ? "border-azul-surelia bg-azul-surelia/10 font-medium text-azul-surelia"
          : "border-transparent text-azul-surelia/70 hover:bg-azul-surelia/5 hover:text-azul-surelia"
      }`}
    >
      {report.title}
    </button>
  );
};

const Reportes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, isOperator, loading } = useUser();

  // Access control: owners see everything; operators see only the reportes
  // flagged `operator`; everyone else is turned away.
  const isFullAccess = isOwner;
  const hasAccess = isFullAccess || isOperator;
  const { asociadosEnabled } = useAsociados();
  const visibleReports = useMemo(
    () =>
      (isFullAccess ? REPORTS : REPORTS.filter((r) => r.operator))
        .filter((r) => !r.asociados || asociadosEnabled),
    [isFullAccess, asociadosEnabled]
  );
  const canSeeReport = (id) => visibleReports.some((r) => r.id === id);

  const [selectedReportId, setSelectedReportId] = useState(() => {
    const requested = new URLSearchParams(location.search).get('reporte');
    return REPORTS.some((r) => r.id === requested) ? requested : REPORTS[0].id;
  });

  const selectedReport = REPORTS.find((r) => r.id === selectedReportId) || REPORTS[0];
  // The tab follows the report, never the other way around: a deep-link to a
  // convenio report opens the Convenios tab, and the export container can
  // render a report from the other tab without moving the user.
  const [activeTab, setActiveTab] = useState(selectedReport.tab);
  useEffect(() => {
    setActiveTab(selectedReport.tab);
  }, [selectedReport.tab]);

  const tabsWithContent = useMemo(
    () => TABS.filter((t) => visibleReports.some((r) => r.tab === t.id)),
    [visibleReports]
  );

  // Sections of the active tab, empty groups dropped (an operator sees fewer
  // cards, and a group can end up with none).
  const sections = useMemo(
    () =>
      GROUPS.filter((g) => g.tab === activeTab)
        .map((g) => ({ ...g, reports: visibleReports.filter((r) => r.group === g.id) }))
        .filter((g) => g.reports.length > 0),
    [activeTab, visibleReports]
  );

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    // Keep the rendered report in step with the tab the user is looking at.
    const first = visibleReports.find((r) => r.tab === tabId);
    if (first && selectedReport.tab !== tabId) setSelectedReportId(first.id);
  };

  // Two-way ?reporte= sync: the URL always names the visible report, and an
  // external URL change (deep-link) switches the report.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reporte') === selectedReportId) return;
    params.set('reporte', selectedReportId);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportId]);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('reporte');
    if (requested && requested !== selectedReportId && canSeeReport(requested)) {
      setSelectedReportId(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const { tarifas, tarifasLoading } = useTarifas();
  const { contractsList } = useContracts();
  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  // En móvil el rail se colapsa detrás de un botón; en lg+ siempre está.
  const [railOpen, setRailOpen] = useState(false);
  // En escritorio el rail se puede esconder para devolverle sus ~240px a los
  // reportes de tablas anchas. La preferencia se recuerda: quien trabaja con
  // un reporte ancho no quiere volver a cerrarlo en cada visita.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      return localStorage.getItem('reportes:rail') === 'collapsed';
    } catch {
      return false;
    }
  });
  const toggleRail = () =>
    setRailCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('reportes:rail', next ? 'collapsed' : 'open');
      } catch {
        /* modo privado: la preferencia sólo dura la sesión */
      }
      return next;
    });
  const [exportingReportId, setExportingReportId] = useState(null);
  const reportContainerRef = useRef(null);

  // Some reportes are wider than a small window or a zoomed browser allows.
  // The layout no longer lets them widen the page (that pushed the scrollbar
  // under the sidebars), so instead we detect the overflow and tell the user
  // what to do (Fran, 2026-10-19). Re-measured on resize and on every report
  // change; the container clips so nothing paints over the rail.
  const [reporteDesborda, setReporteDesborda] = useState(false);
  useEffect(() => {
    const el = reportContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const medir = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const n = reportContainerRef.current;
        if (!n) return;
        setReporteDesborda(n.scrollWidth > n.clientWidth + 2);
      });
    };
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    medir();
    const t = setTimeout(medir, 400); // charts and tables that size themselves after data arrives
    return () => { ro.disconnect(); cancelAnimationFrame(raf); clearTimeout(t); };
  }, [selectedReportId, railCollapsed]);
  const exportContainerRef = useRef(null);

  // The role resolves after the first render, and a deep-link can name a report
  // this user may not open — in both cases fall back to their first report.
  useEffect(() => {
    if (loading || !hasAccess) return;
    if (!canSeeReport(selectedReportId) && visibleReports.length > 0) {
      setSelectedReportId(visibleReports[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasAccess, visibleReports, selectedReportId]);

  // Compute available years from all fianzas
  const yearsAvailable = useMemo(() => {
    const set = new Set();
    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        const fechaMs = getFechaEmision(fianza);
        if (fechaMs) {
          set.add(new Date(fechaMs).getFullYear());
        }
      });
    });
    if (set.size === 0) set.add(now.getFullYear());
    return Array.from(set).sort((a, b) => a - b);
  }, [contractsList, now]);

  // Auto-select valid year when data loads
  useEffect(() => {
    if (yearsAvailable.length > 0 && !yearsAvailable.includes(selectedYear)) {
      setSelectedYear(yearsAvailable[yearsAvailable.length - 1]);
    }
  }, [yearsAvailable, selectedYear]);

  useEffect(() => {
    // Trigger re-render when tarifas finish loading; no-op here, just dependency anchor
  }, [tarifas, tarifasLoading]);

  // Render a report component by ID
  const renderReportById = (reportId) => {
    if (!canSeeReport(reportId)) return null;
    switch (reportId) {
      case "monthly-production":
        return <MonthlyProduction selectedYear={selectedYear} />;
      case "comparativo-anual":
        return <ComparativoAnual selectedYear={selectedYear} />;
      case "comision-afianzadora":
        return <ComisionPorAfianzadora selectedYear={selectedYear} />;
      case "liquidacion-comisiones":
        return <LiquidacionComisiones selectedYear={selectedYear} />;
      case "comision-asociado":
        return <ComisionPorAsociado selectedYear={selectedYear} />;
      case "pendientes":
        return <ReportePendientes />;
      case "cobranza-primas":
        return <CobranzaPrimas />;
      case "saldos-clientes":
        return <SaldosCreditosClientes />;
      case "metas-afianzadora":
        return <MetasPorAfianzadora selectedYear={selectedYear} />;
      case "ciclo-vida":
        return <CicloVidaFianzas selectedYear={selectedYear} />;
      case "beneficiarios":
        return <Beneficiarios selectedYear={selectedYear} />;
      // Los tres primeros de convenios son fotografía de HOY (un requisito
      // atrasado no deja de estarlo al cambiar de año), así que ignoran el
      // selector; sólo el de comisión, que lee el ledger, es por ejercicio.
      case "cumplimiento-convenios":
        return <CumplimientoConvenios />;
      case "proveedores-riesgo":
        return <ProveedoresRiesgo />;
      case "comision-convenio":
        return <ComisionPorConvenio selectedYear={selectedYear} />;
      case "cobertura-proyecto":
        return <CoberturaPorProyecto />;
      case "estadisticas-cliente":
        return <EstadisticasCliente selectedYear={selectedYear} />;
      case "produccion-responsable":
        return <ProduccionPorResponsable selectedYear={selectedYear} />;
      case "produccion-cliente":
        return <ProduccionPorCliente selectedYear={selectedYear} />;
      case "produccion-afianzadora":
        return <ProduccionPorAfianzadora selectedYear={selectedYear} />;
      case "tarifas-cliente":
        return <TarifasPorCliente selectedYear={selectedYear} />;
      default:
        return null;
    }
  };

  // Export multiple reports to a single PDF
  const handleExportReports = useCallback(async (reports, onProgress) => {
    const period = formatPeriod(selectedYear);

    // Create a new PDF document
    const pdfDoc = await createPDFDocument();

    for (let i = 0; i < reports.length; i++) {
      const report = reports[i];

      // Update progress
      onProgress(i + 1, reports.length, report.title);

      // Set the report to render in export container
      setExportingReportId(report.id);

      // Wait for the report to render fully (charts, data, layout)
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Add the report to the PDF
      if (exportContainerRef.current) {
        await pdfDoc.addReport(exportContainerRef.current, {
          title: report.title,
          period
        });
      }
    }

    // Clear the export container
    setExportingReportId(null);

    // Generate filename
    const filename = reports.length === 1
      ? `${reports[0].title.replace(/\s+/g, '_')}_${selectedYear}.pdf`
      : `Reportes_${selectedYear}.pdf`;

    // Save the combined PDF
    pdfDoc.save(filename);
  }, [selectedYear]);

  // Show loading while checking access
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  // Access denied for non-owners
  if (!hasAccess) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
          </div>
          <Heading level={2} className="text-gray-900 mb-2">
            Acceso Restringido
          </Heading>
          <Text className="text-gray-600 mb-6">
            Los reportes solo están disponibles para propietarios de la organización.
          </Text>
          <button
            onClick={() => navigate("/app")}
            className="px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // El año y el export se definen una sola vez y se montan donde toque: en el
  // rail cuando está abierto, junto al título del reporte cuando está colapsado.
  const sinEjercicio = selectedReport.usesYear === false;
  const selectorAno = (
      <select
        value={selectedYear}
        onChange={(e) => setSelectedYear(Number(e.target.value))}
        disabled={sinEjercicio}
        title={sinEjercicio ? 'Este reporte es una fotografía de hoy, no de un ejercicio' : undefined}
        className={`w-full rounded-lg border border-azul-surelia/20 bg-white px-3 py-1.5 text-sm font-semibold text-azul-surelia focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/30 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 ${
          railCollapsed ? 'lg:w-auto' : ''
        }`}
      >
        {yearsAvailable.map((yr) => (
          <option key={yr} value={yr}>
            {yr}
          </option>
        ))}
      </select>
  );

  // Every report in the export dialog is comisión o tarifa, so the button
  // follows the same gate.
  const botonExport = isFullAccess ? (
    <button
      onClick={() => setIsExportDialogOpen(true)}
      className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border border-azul-surelia/20 bg-white px-3 py-2 text-sm font-medium text-azul-surelia transition-all duration-200 hover:border-azul-surelia/40 hover:bg-azul-surelia/5 ${
        railCollapsed ? 'lg:w-auto' : ''
      }`}
      title="Exportar reportes a PDF"
    >
      <DocumentArrowDownIcon className="h-5 w-5" />
      <span>Exportar PDF</span>
    </button>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* Rail de reportes. Pegado al viewport: sólo la columna del reporte
            scrollea. La lista se lleva el sobrante y el pie queda clavado
            abajo, así que los controles nunca se salen de vista. */}
        <div
          className={`lg:sticky lg:top-2 lg:flex lg:max-h-[calc(100vh-4rem)] lg:shrink-0 lg:flex-col ${
            railCollapsed ? 'lg:w-10' : 'lg:w-60'
          }`}
          data-pdf-hide
        >
          {railCollapsed && (
            <button
              type="button"
              onClick={toggleRail}
              title="Mostrar la lista de reportes"
              aria-label="Mostrar la lista de reportes"
              className="hidden rounded-lg border border-azul-surelia/20 bg-white p-2 text-azul-surelia transition-colors hover:border-azul-surelia/40 hover:bg-azul-surelia/5 lg:block"
            >
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </button>
          )}

          <div className={railCollapsed ? 'lg:hidden' : 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col'}>
            <div className="mb-2 hidden justify-end lg:flex">
              <button
                type="button"
                onClick={toggleRail}
                title="Ocultar la lista de reportes"
                aria-label="Ocultar la lista de reportes"
                className="rounded-lg border border-azul-surelia/20 bg-white p-1.5 text-azul-surelia transition-colors hover:border-azul-surelia/40 hover:bg-azul-surelia/5"
              >
                <ChevronDoubleLeftIcon className="h-4 w-4" />
              </button>
            </div>

            <label className="mb-3 block">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-500">
                Ejercicio
              </span>
              {selectorAno}
            </label>

            <button
              type="button"
              onClick={() => setRailOpen((o) => !o)}
              className="flex w-full items-center justify-between rounded-lg border border-azul-surelia/20 bg-white px-3 py-2 text-sm font-medium text-azul-surelia lg:hidden"
            >
              <span className="truncate">{selectedReport.title}</span>
              <ChevronDownIcon
                className={`h-4 w-4 shrink-0 transition-transform ${railOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Los tabs quedan fuera del <nav>: son el encabezado de la lista,
                no un elemento más de ella, y no deben scrollear con ella. */}
            {tabsWithContent.length > 1 && (
              <div
                className={`${railOpen ? 'flex' : 'hidden'} mt-2 gap-4 border-b border-azul-surelia/15 lg:mt-0 lg:flex`}
              >
                {tabsWithContent.map((tab) => {
                  const isActive = tab.id === activeTab;
                  const count = visibleReports.filter((r) => r.tab === tab.id).length;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => selectTab(tab.id)}
                      className={`-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-azul-surelia text-azul-surelia'
                          : 'border-transparent text-azul-surelia/50 hover:text-azul-surelia/80'
                      }`}
                    >
                      {tab.label}
                      <span className="ml-1.5 text-xs text-zinc-400 tabular-nums">{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <nav
              className={`${railOpen ? 'block' : 'hidden'} scrollbar-none mt-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:block`}
              aria-label="Reportes disponibles"
            >
              <div className="space-y-4">
                {sections.map((section) => (
                  <div key={section.id}>
                    <Text className="mb-1 px-2.5 text-[11px] uppercase tracking-wide text-gray-500">
                      {section.title}
                    </Text>
                    <div className="space-y-0.5">
                      {section.reports.map((report) => (
                        <ReportRow
                          key={report.id}
                          report={report}
                          selected={report.id === selectedReportId}
                          onSelect={(id) => {
                            setSelectedReportId(id);
                            setRailOpen(false);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </nav>

            {botonExport && (
              <div className="mt-3 border-t border-azul-surelia/15 pt-3">{botonExport}</div>
            )}
          </div>
        </div>

        {/* Reporte */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Cada reporte pinta su propio encabezado; aquí sólo van los
              controles que se quedaron sin casa al colapsar el rail. */}
          {railCollapsed && (
            <div className="hidden items-center justify-end gap-2 lg:flex" data-pdf-hide>
              {selectorAno}
              {botonExport}
            </div>
          )}
          {reporteDesborda && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-pdf-hide>
              Este reporte necesita más ancho del que tiene la ventana. Colapsa la barra lateral, quita el zoom del navegador o agranda la ventana para verlo completo.
            </div>
          )}
          <div
            ref={reportContainerRef}
            className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4 overflow-x-auto"
          >
            {renderReportById(selectedReportId)}
          </div>
        </div>
      </div>

      {/* Hidden export container - renders reports off-screen for PDF capture */}
      {exportingReportId && (
        <div
          style={{
            position: 'fixed',
            left: '-9999px',
            top: 0,
            width: '1100px',
            backgroundColor: '#ffffff',
          }}
        >
          <div
            ref={exportContainerRef}
            className="bg-white p-6"
            style={{ minHeight: '500px' }}
          >
            {renderReportById(exportingReportId)}
          </div>
        </div>
      )}

      {/* Export Dialog */}
      {isFullAccess && (
        <ExportPDFDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          year={selectedYear}
          currentReportId={selectedReportId}
          onExportReports={handleExportReports}
        />
      )}
    </div>
  );
};

export default Reportes;
